import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { fetchOrders } from '../adminApi'
import { isIsoDate } from '../../core/format/dates'
import { asChannel, asStatusFilter } from './orderLabels'
import type { AdminOrderQuery } from '../../core/types/adminRequests'
import type { RequestOptions } from '../../core/types/backend'

const SEARCH_DELAY_MS = 300
const FILTER_KEYS = ['q', 'status', 'kanaal', 'van', 'tot', 'pagina'] as const

/**
 * De lijst met bestellingen. Alle filters staan in het adres (?q=&status=&kanaal=&van=&tot=&pagina=),
 * zodat terug uit een bestelling precies dezelfde lijst geeft. Zoeken wacht even tot je stopt met typen.
 */
export function useOrders() {
  const [params, setParams] = useSearchParams()

  const query = useMemo<AdminOrderQuery>(() => {
    const page = Number(params.get('pagina'))
    const from = params.get('van')
    const to = params.get('tot')
    return {
      q: params.get('q')?.trim() || undefined,
      status: asStatusFilter(params.get('status')),
      channel: asChannel(params.get('kanaal')),
      from: isIsoDate(from) ? from : undefined,
      to: isIsoDate(to) ? to : undefined,
      page: Number.isInteger(page) && page > 1 ? page : 1,
    }
  }, [params])

  // Eén sleutel voor de aanvraag: dezelfde filters = dezelfde lijst, geen dubbele aanvraag.
  const key = JSON.stringify(query)
  const load = useMemo(() => {
    const q = JSON.parse(key) as AdminOrderQuery
    return (opts: RequestOptions) => fetchOrders(q, opts)
  }, [key])
  const { data, status, reload } = useLatestLoad(load)

  /** Eén filter zetten; de pagina gaat dan terug naar 1. Leeg = filter weg. */
  const setFilter = useCallback(
    (name: (typeof FILTER_KEYS)[number], value: string | undefined) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set(name, value)
          else next.delete(name)
          if (name !== 'pagina') next.delete('pagina')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  const clearFilters = useCallback(() => setParams({}, { replace: true }), [setParams])
  const hasFilters = FILTER_KEYS.some((k) => k !== 'pagina' && params.get(k))

  // Zoekveld: direct zichtbaar wat je typt, de lijst volgt na een korte pauze.
  const [search, setSearch] = useState(query.q ?? '')
  useEffect(() => setSearch(query.q ?? ''), [query.q])
  useEffect(() => {
    if (search.trim() === (query.q ?? '')) return
    const t = window.setTimeout(() => setFilter('q', search.trim() || undefined), SEARCH_DELAY_MS)
    return () => window.clearTimeout(t)
  }, [search, query.q, setFilter])

  return { query, page: data, status, reload, setFilter, clearFilters, hasFilters, search, setSearch }
}
